try {
  await import(new URL('bl-personalisation-font-loader.js', import.meta.url).href);
} catch (error) {
  console.error('Exact personalisation fonts could not be initialised.', error);
}

(() => {
  const KEY = '__blPersonalisationTextPreview';
  const SIX = ['Alice Bold', 'Bree Serif Bold', 'Arial Bold', 'Salsa Bold', 'Milonga Bold', 'Monotype Corsiva Bold'];
  const DEFAULT = { family: 'Arial, sans-serif', weight: '400', style: 'normal' };

  const TEXT = {
    'Alice Bold': { family: '"BL Alice", serif', weight: '700', style: 'normal' },
    'Bree Serif Bold': { family: '"BL Bree Serif", serif', weight: '700', style: 'normal' },
    'Arial Bold': { family: 'Arial, sans-serif', weight: '700', style: 'normal' },
    'Salsa Bold': { family: '"BL Salsa", cursive', weight: '700', style: 'normal' },
    'Milonga Bold': { family: '"BL Milonga", cursive', weight: '700', style: 'normal' },
    'Monotype Corsiva Bold': { family: '"BL Monotype Corsiva", cursive', weight: '700', style: 'normal' },
  };

  const ENGRAVING = {
    Arial: DEFAULT,
    Alice: { family: '"BL Alice", serif', weight: '400', style: 'normal' },
    'Monotype Corsiva': { family: '"BL Monotype Corsiva", cursive', weight: '400', style: 'normal' },
    'Lobster Two': { family: '"BL Lobster Two", cursive', weight: '400', style: 'normal' },
    'Astyca Script': { family: '"Astyca Script", cursive', weight: '400', style: 'normal' },
    Salsa: { family: '"BL Salsa", cursive', weight: '400', style: 'normal' },
    Milonga: { family: '"BL Milonga", cursive', weight: '400', style: 'normal' },
    Blenda: { family: '"Dancing Script", cursive', weight: '400', style: 'normal' },
    Harrington: { family: '"BL Milonga", cursive', weight: '400', style: 'normal' },
    Bookman: { family: '"Bookman Old Style", Bookman, serif', weight: '400', style: 'normal' },
    'Bookman Old Style': { family: '"Bookman Old Style", Bookman, serif', weight: '400', style: 'normal' },
    Aubrey: { family: '"Aubrey", cursive', weight: '400', style: 'normal' },
  };

  const hasComforter = (root) => Boolean(
    root.querySelector('[data-pb-comforter-preview], [data-bl-comforter-preview], [data-bl-embroidery-font]'),
  );

  const usesSix = (root) => {
    const hasPreview = Boolean(root.querySelector('[data-bl-text-preview], [data-pb-comforter-preview], [data-bl-comforter-preview]'));
    const method = (root.dataset.method || '').trim().toLowerCase();
    return hasPreview && (method !== 'engraving' || hasComforter(root));
  };

  const fontStyle = (root, name) => (usesSix(root) ? TEXT[name] : ENGRAVING[name]) || DEFAULT;

  const enforceOptions = (root) => {
    if (!usesSix(root) || hasComforter(root)) return;
    const select = root.querySelector('[data-bl-font]');
    if (!select) return;

    const expected = ['', ...SIX];
    const current = [...select.options].map((option) => option.value);
    if (current.length === expected.length && current.every((value, index) => value === expected[index])) return;

    const previous = select.value;
    select.replaceChildren(new Option('Choose a font', ''), ...SIX.map((font) => new Option(font, font)));
    select.value = SIX.includes(previous) ? previous : '';
  };

  const isLight = (hex = '') => {
    const value = hex.replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(value)) return false;
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16));
    return ((299 * r + 587 * g + 114 * b) / 1000) > 210;
  };

  const applyStyle = (element, style) => {
    if (!element) return;
    element.style.fontFamily = style.family;
    element.style.fontWeight = style.weight;
    element.style.fontStyle = style.style;
  };

  const fit = (box, output) => {
    if (!box || !output) return;
    let size = matchMedia('(max-width: 480px)').matches ? 42 : 48;
    output.style.fontSize = `${size}px`;

    requestAnimationFrame(() => {
      const width = Math.max(box.clientWidth - 32, 1);
      const height = Math.max(box.clientHeight - 24, 1);
      let guard = 0;
      while ((output.scrollWidth > width || output.scrollHeight > height) && size > 18 && guard < 80) {
        size -= 1;
        output.style.fontSize = `${size}px`;
        guard += 1;
      }
    });
  };

  const updateTextPreview = (root) => {
    const box = root.querySelector('[data-bl-text-preview]');
    const output = root.querySelector('[data-bl-text-preview-value]');
    if (!box || !output) return;

    const text = [...root.querySelectorAll('[data-bl-text-line]')]
      .map((input) => input.value.replace(/\s+$/, ''))
      .join('\n')
      .replace(/\n+$/, '');
    const selectedFont = root.querySelector('[data-bl-font]')?.value || '';
    const colour = root.querySelector('[data-bl-colour]:checked')?.dataset.colourHex || '#111111';

    output.textContent = text || 'Your name';
    applyStyle(output, fontStyle(root, selectedFont));
    output.style.color = colour;
    box.dataset.empty = text ? 'false' : 'true';
    box.dataset.lightText = String(isLight(colour));
    fit(box, output);
  };

  const updatePlaquePreview = (root) => {
    const select = root.querySelector('[data-bl-font]');
    const output = root.querySelector('[data-bl-preview-text]');
    if (!select || !output) return;
    applyStyle(output, ENGRAVING[select.value] || DEFAULT);
  };

  const updateComforterPreview = (root) => {
    const select = root.querySelector('[data-bl-embroidery-font]');
    const box = root.querySelector('[data-pb-comforter-preview], [data-bl-comforter-preview]');
    const output = root.querySelector('[data-pb-comforter-preview-value]');
    if (!select || !box || !output) return;
    applyStyle(output, TEXT[select.value] || DEFAULT);
    fit(box, output);
  };

  const updateLegacyDualPreview = (root) => {
    const select = root.querySelector('[data-dual-comforter-font]');
    const box = root.querySelector('[data-dual-preview]');
    const output = root.querySelector('[data-dual-preview-text]');
    if (!select || !box || !output) return;

    const style = TEXT[select.value]
      || ENGRAVING[select.value]
      || DEFAULT;
    applyStyle(output, style);
    fit(box, output);
  };

  const update = (root) => {
    enforceOptions(root);
    updateTextPreview(root);
    updatePlaquePreview(root);
    updateComforterPreview(root);
    updateLegacyDualPreview(root);
  };

  const bind = (root) => {
    if (root.dataset.blTextPreviewBound === 'true') return update(root);
    root.dataset.blTextPreviewBound = 'true';

    const refresh = () => update(root);
    root.addEventListener('input', refresh);
    root.addEventListener('change', refresh);
    update(root);
    document.fonts?.ready?.then(refresh);
  };

  const bindAll = () => document.querySelectorAll('bl-personalisation').forEach(bind);
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      bindAll();
    });
  };

  window[KEY] = { bindAll };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', bindAll, { once: true })
    : bindAll();
  document.addEventListener('shopify:section:load', queue);
  addEventListener('resize', queue);
  new MutationObserver(queue).observe(document.documentElement, { childList: true, subtree: true });
})();
